import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface StrategyTriggerDetail {
  strategy_name: string;
  score_contribution: number;
  reasoning: string | null;
  pattern_type: string | null;
  pattern_name: string | null;
  success_rate: number | null;
}

export interface PredictionDiagnostics {
  triggers: StrategyTriggerDetail[];
  hasStoredTriggers: boolean;
}

/**
 * Loads per-prediction strategy diagnostics:
 *  - rows from prediction_strategy_triggers (strategy_name, score_contribution, reasoning)
 *  - mapped to the relevant pattern_type / success_rate via the patterns table
 *
 * When no stored trigger rows exist, returns an empty list and the caller
 * falls back to parsing strategy_signals / explanation.
 */
export function usePredictionDiagnostics(predictionId: string | undefined, enabled = true) {
  return useQuery<PredictionDiagnostics>({
    queryKey: ["prediction-diagnostics", predictionId],
    enabled: enabled && !!predictionId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data: triggerRows, error } = await supabase
        .from("prediction_strategy_triggers")
        .select("strategy_name, score_contribution, reasoning")
        .eq("prediction_id", predictionId!)
        .order("score_contribution", { ascending: false });

      if (error) throw error;

      const rows = triggerRows || [];
      if (rows.length === 0) {
        return { triggers: [], hasStoredTriggers: false };
      }

      // Map strategy names to pattern metadata (best-effort substring match).
      const { data: patterns } = await supabase
        .from("patterns")
        .select("pattern_name, pattern_type, success_rate");

      const patternList = patterns || [];
      const findPattern = (strategyName: string) => {
        const sn = strategyName.toLowerCase();
        return patternList.find(
          (p) =>
            p.pattern_name &&
            (sn.includes(p.pattern_name.toLowerCase()) ||
              p.pattern_name.toLowerCase().includes(sn.split(" ")[0]!)),
        );
      };

      const triggers: StrategyTriggerDetail[] = rows.map((r) => {
        const matched = findPattern(r.strategy_name);
        return {
          strategy_name: r.strategy_name,
          score_contribution: Number(r.score_contribution) || 0,
          reasoning: r.reasoning,
          pattern_type: matched?.pattern_type ?? null,
          pattern_name: matched?.pattern_name ?? null,
          success_rate: matched?.success_rate != null ? Number(matched.success_rate) : null,
        };
      });

      return { triggers, hasStoredTriggers: true };
    },
  });
}
